import { useCallback, useEffect, useRef } from "react";

const MESSAGE_SOURCE = "ClassRegistrationCheckout.jsx";
const PURCHASE_STORAGE_PREFIX = "classCheckoutPurchaseTracked:";

export const postToParent = (command, data) => {
  if (typeof window === "undefined" || window.parent === window) return;
  window.parent.postMessage({ from: MESSAGE_SOURCE, command, data }, "*");
};

const purchaseAlreadyTracked = (orderNumber) => {
  try {
    return window.sessionStorage.getItem(`${PURCHASE_STORAGE_PREFIX}${orderNumber}`) === "1";
  } catch (error) {
    return false;
  }
};

const rememberPurchaseTracked = (orderNumber) => {
  try {
    window.sessionStorage.setItem(`${PURCHASE_STORAGE_PREFIX}${orderNumber}`, "1");
  } catch (error) {
    return;
  }
};

export default function useClassCheckoutAnalytics({ googleTrackingIds = [], analyticsItem, currencyCode, pageLocation }) {
  const gtag = useRef(null);
  const trackingKey = googleTrackingIds.join(",");

  useEffect(() => {
    if (typeof window === "undefined" || !trackingKey) return;
    window.dataLayer = window.dataLayer || [];
    gtag.current = function () { window.dataLayer.push(arguments); };
    gtag.current("js", new Date());
    if (pageLocation) gtag.current("set", { page_location: pageLocation });
    trackingKey.split(",").forEach((trackingId) => gtag.current("config", trackingId, { cookie_flags: "secure;samesite=none" }));
  }, [trackingKey, pageLocation]);

  const items = useCallback((summary) => [{
    item_id: String(analyticsItem.id),
    item_name: analyticsItem.name,
    item_brand: analyticsItem.venueName,
    price: parseFloat(summary.unit_price),
    quantity: summary.quantity,
  }], [analyticsItem]);

  const beginCheckout = useCallback((order) => {
    if (!analyticsItem) return;
    try {
      const payload = { currency: currencyCode, value: parseFloat(order.total), items: items(order) };
      if (gtag.current) gtag.current("event", "begin_checkout", payload);
      postToParent("ga.begin_checkout", payload);
    } catch (error) {
      console.error("Could not record begin_checkout", error);
    }
  }, [analyticsItem, currencyCode, items]);

  const purchase = useCallback((orderStatus) => {
    if (!analyticsItem) return;
    if (purchaseAlreadyTracked(orderStatus.order_number)) return;
    rememberPurchaseTracked(orderStatus.order_number);
    const payload = {
      transaction_id: orderStatus.order_number,
      currency: currencyCode,
      value: parseFloat(orderStatus.total),
      tax: parseFloat(orderStatus.taxes),
      shipping: 0,
      items: items(orderStatus),
    };
    if (gtag.current) gtag.current("event", "purchase", payload);
    postToParent("ga.purchase", payload);
  }, [analyticsItem, currencyCode, items]);

  return { beginCheckout, purchase };
}
